// ─── Collage Element: Image Card ──────────────────────────────
// Polaroid frame or plain photo card. Positioned absolutely on canvas.
// Applies customStyle overrides (borderRadius, opacity, widthOverride).

import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { Upload } from 'lucide-react';
import { getElementFrameStyle } from '../../../lib/utils/collageLayout';
import { useCollageElementMotion } from '../../../lib/utils/collageMotion';
import { CollageEditContext } from '../CollageEditContext';

function ImagePlaceholder({ onUpload, aspectRatio }) {
  const editCtx = useContext(CollageEditContext);

  const handleClick = (e) => {
    if (!editCtx) return;
    e.stopPropagation();
    // Trigger file picker
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (ev) => {
      const file = ev.target.files?.[0];
      if (file && onUpload) onUpload(file);
    };
    input.click();
  };

  return (
    <div
      onClick={editCtx ? handleClick : undefined}
      className="w-full flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800/50"
      style={{ cursor: editCtx ? 'pointer' : 'default', aspectRatio: aspectRatio || '3/4' }}
      data-testid="image-placeholder-upload"
    >
      <Upload className="w-6 h-6 text-gray-400" />
      <span className="text-xs text-gray-400 font-medium">Click to upload</span>
    </div>
  );
}

export default function ImageCard({ element, isMobile, theme, skipPositioning }) {
  let style = skipPositioning ? {} : getElementFrameStyle(element.layout, isMobile);
  const rotation = `${element.rotation || 0}deg`;
  const motionProps = useCollageElementMotion(element);
  const cs = element.customStyle || {};
  const editCtx = useContext(CollageEditContext);
  const hasImage = element.src && element.src.length > 0;

  // Phase 1.6 — soften large repaint-heavy shadows on mobile during scroll.
  // The wider 50px blur is kept on desktop where GPU compositing handles it
  // fine. Editor preview still uses the full shadow so designers see truth.
  const mobileShadow = '0 6px 18px rgba(0,0,0,0.08)';
  const desktopShadow = theme.shadowSoft || '0 16px 50px rgba(0,0,0,0.10)';
  const effectiveShadow = (isMobile && !editCtx) ? mobileShadow : desktopShadow;

  // Aspect: 'original' uses the photo's intrinsic shape (saved on upload as naturalAspect),
  // otherwise uses the picker value (e.g., '1/1', '4/5', '16/9').
  const aspectMode = cs.aspectMode || 'original';
  const naturalAspect = element.naturalAspect; // string like "1.3333"
  const isLargeFallback = element.style === 'plain-photo-large';
  const fallbackAspect = element.style === 'polaroid' ? '1/1' : (isLargeFallback ? '4/5' : '3/4');
  const resolvedAspect =
    aspectMode === 'original' && naturalAspect ? String(naturalAspect)
    : aspectMode !== 'original' ? aspectMode
    : fallbackAspect;

  // Upload handler — uploads to the RSVP image endpoint
  const handleUpload = async (file) => {
    if (!editCtx?.onImageUpload) return;
    editCtx.onImageUpload(element.id, file);
  };

  const imgStyle = {
    // Skip default shadow + cropping behaviour for transparent images (monograms, ornaments, logos)
    boxShadow: cs.transparent ? 'none' : effectiveShadow,
    borderRadius: cs.borderRadius ? `${cs.borderRadius}px` : undefined,
    opacity: cs.opacity !== undefined ? cs.opacity : undefined,
    aspectRatio: resolvedAspect,
    objectFit: cs.transparent ? 'contain' : 'cover',
    objectPosition: 'center',
    background: 'transparent',
  };

  if (element.style === 'polaroid') {
    return (
      <motion.div
        {...motionProps}
        style={{ ...style, rotate: rotation }}
        className="select-none"
      >
        <div
          className="bg-[#fdfbf8] p-3 sm:p-4"
          style={{
            boxShadow: effectiveShadow,
            borderRadius: cs.borderRadius ? `${cs.borderRadius}px` : undefined,
            opacity: cs.opacity !== undefined ? cs.opacity : undefined,
          }}
        >
          {hasImage ? (
            <img
              src={element.src}
              alt={element.caption || ''}
              className="w-full"
              style={{
                aspectRatio: resolvedAspect,
                objectFit: 'cover',
                objectPosition: 'center',
                borderRadius: cs.borderRadius ? `${Math.max(0, cs.borderRadius - 4)}px` : undefined,
              }}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <ImagePlaceholder onUpload={handleUpload} aspectRatio={resolvedAspect} />
          )}
          {element.caption && (
            <div
              className="px-1 pb-1 pt-3 text-center text-[10px] uppercase tracking-[0.18em] sm:text-[11px]"
              style={{ color: theme.accent, fontFamily: theme.monoFont || theme.bodyFont }}
            >
              {element.caption}
            </div>
          )}
        </div>
      </motion.div>
    );
  }

  // Plain photo or large photo
  return (
    <motion.div
      {...motionProps}
      style={{ ...style, rotate: rotation }}
      className="select-none"
    >
      {hasImage ? (
        <img
          src={element.src}
          alt={element.caption || ''}
          className="w-full"
          style={imgStyle}
          loading="lazy"
          decoding="async"
        />
      ) : (
        <ImagePlaceholder onUpload={handleUpload} aspectRatio={resolvedAspect} />
      )}
    </motion.div>
  );
}
